import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, Home, AlertCircle } from "lucide-react";
import { trackPageVisit, startPageTracking } from '@/utils/analytics';
import { Helmet } from "react-helmet";

const canonicalUrl = "https://nextgenstartups.com/404";
const seo = {
  title: "404 Not Found | NextGen Startups",
  description: "Sorry, the page you are looking for does not exist. Explore AI-powered business solutions at NextGen Startups.",
  url: canonicalUrl,
  ogImage: "/assets/og-image.png",
};

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    // Start tracking and track 404 errors with duration
    startPageTracking();
    const cleanup = () => {
      trackPageVisit(`404: ${location.pathname}`);
    };
    
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );

    return cleanup;
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAFAFA] px-4">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.url} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:image" content={seo.ogImage} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.ogImage} />
        <meta name="twitter:url" content={seo.url} />
      </Helmet>
      <div className="max-w-md w-full bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-[#3D2E54] px-6 py-8 text-white">
          <div className="flex items-center justify-center mb-4">
            <AlertCircle className="h-10 w-10" />
          </div>
          <h1 className="text-3xl font-bold text-center">404</h1>
          <p className="text-lg text-center mt-2 opacity-90">Page Not Found</p>
        </div>
        
        <div className="px-6 py-8">
          <p className="text-[#3D2E54] mb-6">
            The page you're looking for doesn't exist or has been moved. 
            We apologize for the inconvenience.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <Link 
              to="/"
              className="flex items-center justify-center gap-2 px-5 py-3 bg-[#3D2E54] text-white rounded-md font-medium transition-colors hover:bg-[#533e72] w-full"
            >
              <Home className="h-4 w-4" />
              Go to Homepage
            </Link>
            
            <button 
              onClick={() => window.history.back()}
              className="flex items-center justify-center gap-2 px-5 py-3 border border-[#C3B8F6] text-[#3D2E54] rounded-md font-medium transition-colors hover:bg-[#F7F5F2] w-full mt-3 sm:mt-0"
            >
              <ArrowLeft className="h-4 w-4" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
